import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {LABEL_FLOAT, LAYOUT_TYPE_FULL, MINO_STYLE, NAV_STYLE_DRAWER, NAV_STYLE_FIXED, TAB_SIZE, THEME_TYPE_SEMI_DARK} from "constants/ThemeSetting";
import {
    etkinMenuDuzeni,
    gecerliKullaniciId,
    gorunumBirlestir,
    gorunumDogrula,
    gorunumOku,
    GorunumTercihi,
    GorunumYamasi
} from "../../core/gorunum/gorunumTercihi";

interface themeModel {
    navStyle: string,
    layoutType: string,
    themeType: string,
    themeColor: string,
    isDirectionRTL: boolean
    navCollapsed: boolean,
    width: number,
    labelType:string,
    menuFold: boolean,
    fullScreen:boolean,
    /** kişisel görünüm tercihi (core/gorunum/gorunumTercihi.ts); navStyle ve menuFold buradan türetilir */
    gorunum: GorunumTercihi,
    /** tam ekran menü (başlatıcı) açık mı: yalnız masaüstünde tamEkran düzeninde (baslaticiEtkin) true olabilir */
    baslaticiAcik: boolean,
}

/**
 * tercihten türetilen alanlar: kenar çubuğunun daraltma durumu için TEK KAYNAK. Menüsüz ve tam ekran düzenleri
 * NAV_STYLE_DRAWER: telefonda ikisi de çekmece; masaüstünde menüsüzde çekmece, tam ekranda başlatıcı (baslaticiEtkin).
 */
const turetilmis = (gorunum: GorunumTercihi) => {
    const duzen = etkinMenuDuzeni(gorunum);
    return {navStyle: duzen === "gizli" || duzen === "tamEkran" ? NAV_STYLE_DRAWER : NAV_STYLE_FIXED, menuFold: duzen === "dar"};
};

/**
 * Tam ekran menü (başlatıcı) etkin mi: tamEkran düzeni ve masaüstü genişliği (≥ TAB_SIZE). O zaman kenar çubuğu da
 * çekmece de kurulmaz; Main başlatıcıyı kurar, üst çubuktaki düğme onu açar. Telefonda her düzende çekmece.
 */
export const baslaticiEtkin = (theme: { gorunum: GorunumTercihi, width: number }): boolean =>
    etkinMenuDuzeni(theme.gorunum) === "tamEkran" && theme.width >= TAB_SIZE;

/**
 * Düzen ya da genişlik değişince: başlatıcı etkin değilse kapanır (genişleyince kendiliğinden açılmaz). Etkinse
 * çekmece (Drawer) kurulu değildir: telefonda açık kalmış çekmecenin durumu silinir, yoksa yeniden daralınca
 * çekmece bir an açık kurulup odağı içine alırdı.
 */
const baslaticiDurumUygula = (state: themeModel) => {
    if (baslaticiEtkin(state)) {
        state.navCollapsed = false;
    } else {
        state.baslaticiAcik = false;
    }
};

/** tercih değişince türetilmiş alanlar; tam ekran dışındaki düzene geçince başlatıcı kapanır */
const turetilmisUygula = (state: themeModel) => {
    Object.assign(state, turetilmis(state.gorunum));
    baslaticiDurumUygula(state);
};

const ilkGorunum = gorunumOku(gecerliKullaniciId());

const initialState:themeModel = {
    navStyle: turetilmis(ilkGorunum).navStyle,
    layoutType: LAYOUT_TYPE_FULL,
    themeType: THEME_TYPE_SEMI_DARK,
    themeColor:MINO_STYLE,
    isDirectionRTL: false,
    // true = çekmece (Drawer) açık: sayfa açılışında KAPALI gelir
    navCollapsed: false,
    width: window.innerWidth,
    // labelType: LABEL_NORMAL
    labelType: LABEL_FLOAT,
    menuFold: turetilmis(ilkGorunum).menuFold,
    fullScreen:false,
    gorunum: ilkGorunum,
    baslaticiAcik: false,
};

export const themeSlice = createSlice({
    name: "theme",
    initialState,
    reducers: {
        setDirectionRTL: (state, action: PayloadAction<any>) => {
            state.isDirectionRTL = action.payload;
        },
        toggleCollapsedSideNav: (state, action:PayloadAction<boolean>) => {
            state.navCollapsed = action.payload;
        },
        updateWindowWidth: (state, action:PayloadAction<number>) => {
            state.width = action.payload;
            // başlatıcı yalnız masaüstünde: telefon genişliğine inince kapanır (genişleyince kendiliğinden açılmasın)
            baslaticiDurumUygula(state);
        },
        setFullScreen: (state, action:PayloadAction<boolean>) => {
            state.fullScreen = action.payload;
        },
        /**
         * kullanıcının seçimi: verilen alanlar birleştirilir, `bildirim` bir kat derin (ör. `setGorunum({bildirim: {ses: true}})`
         * yalnız sesi değiştirir). Görünüm ekranı, kenar çubuğunun daraltma düğmesi, sekme sağ tık menüsü
         */
        setGorunum: (state, action:PayloadAction<GorunumYamasi>) => {
            state.gorunum = gorunumBirlestir(state.gorunum, action.payload);
            turetilmisUygula(state);
        },
        /** tercih TÜMÜYLE değişir: giriş / kullanıcı değişimi, sunucudan ya da başka sekmeden gelen değer */
        gorunumYukle: (state, action:PayloadAction<GorunumTercihi>) => {
            state.gorunum = gorunumDogrula(action.payload);
            turetilmisUygula(state);
        },
        /** tam ekran menüyü (başlatıcı) aç / kapat: sol üstteki düğme, Ctrl/Cmd+K, ekran seçimi, Esc; etkin değilken hep kapalı */
        setBaslaticiAcik: (state, action:PayloadAction<boolean>) => {
            state.baslaticiAcik = action.payload && baslaticiEtkin(state);
        },
    }
})

// menuFold yalnız tercihten türetilir (setMenuFold kaldırıldı: ikinci kaynak olmasın)
export const { setDirectionRTL, toggleCollapsedSideNav, updateWindowWidth, setFullScreen, setGorunum, gorunumYukle, setBaslaticiAcik } = themeSlice.actions;

export default themeSlice.reducer;
